// /wishes/trash — the soft-deleted wishes (W39.54).
//
// The screen exists because `DELETE /api/v1/wishes/{id}` was advertised as
// 「restorable via /wishes/{id}/restore」 while there was no way to find the id
// again and nothing ever removed the row. The listing, the restore and the
// permanent delete are all in `lib/api/wishlist.ts`; this file is the screen.
//
// The rows and the confirm live in `components/system/TrashList.tsx`, shared
// with `/anniversaries/trash` — the two differ only in the field the thing is
// called (`name` vs `title`) and that is normalised below rather than threaded
// through the component.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { TrashList, type TrashRow } from "@/components/system/TrashList";
import { PageErrorBoundary } from "@/components/ErrorBoundary";
import { listWishTrash, purgeWish, restoreWish } from "@/lib/api/wishlist";
import { ApiError } from "@/lib/api/pair";
import { appErrorMessage } from "@/lib/i18n/app";
import { WISH_RESTORE_DAYS } from "@/lib/retentionWindows";

/** The same key `Wishes.tsx` queries with, so invalidating it here actually
 *  clears the list the user is about to go back to. It is a local const in
 *  that file rather than an export; duplicating the literal is deliberate over
 *  importing a screen, and a comment here says where it came from. */
const WISHES_QUERY_KEY = ["wishes", "list", { visibility: "all" }] as const;

export function WishTrashPage() {
  const qc = useQueryClient();

  const trash = useQuery({ queryKey: ["wishes", "trash"], queryFn: listWishTrash });

  const afterChange = () => {
    void qc.invalidateQueries({ queryKey: ["wishes", "trash"] });
    // The keys Wishes.tsx itself uses, not invented ones: a restored wish
    // belongs back in the list, and a purged one must not linger in a cached
    // page. Naming keys that nothing else invalidates would leave the screen
    // agreeing with itself and disagreeing with everything else.
    void qc.invalidateQueries({ queryKey: ["wishes", "list"] });
    void qc.invalidateQueries({ queryKey: WISHES_QUERY_KEY });
  };

  const restoreMut = useMutation({
    mutationFn: restoreWish,
    onSuccess: afterChange,
  });
  const purgeMut = useMutation({ mutationFn: purgeWish, onSuccess: afterChange });

  const rows: TrashRow[] = (trash.data ?? []).map((w) => ({
    id: w.id,
    label: w.name,
    daysLeft: w.days_left,
    canRestore: w.can_restore,
  }));

  return (
    <>
      <TrashList
        noun="心願垃圾桶"
        itemNoun="心願"
        parentTo="/wishes"
        parentLabel="心願"
        retentionDays={WISH_RESTORE_DAYS}
        rows={rows}
        isLoading={trash.isLoading}
        isError={trash.isError}
        errorMessage={
          trash.error instanceof ApiError
            ? appErrorMessage(trash.error.code, trash.error.message)
            : "載入垃圾桶失敗，請稍後再試。"
        }
        onRetry={() => void trash.refetch()}
        onRestore={async (id) => {
          await restoreMut.mutateAsync(id);
        }}
        onPurge={async (id) => {
          await purgeMut.mutateAsync(id);
        }}
      />
    </>
  );
}

export function WishTrashRoute() {
  return (
    <PageErrorBoundary label="心願垃圾桶">
      <WishTrashPage />
    </PageErrorBoundary>
  );
}
